import React, { useCallback, useEffect, useState } from "react";
import toast from "react-hot-toast";
import { useParams } from "react-router-dom";

import {
    Box,
    Grid,
    Typography,
    IconButton,
} from "@mui/material";
import LoadingButton from "@mui/lab/LoadingButton";
import CloseIcon from "@mui/icons-material/Close";

import {
    Stepper,
    LoadingBar,
    BreadCrumbs,
    ExitConfirmationModal,
} from "../Common";

import {
    trakrefFontFamily,
    trakrefFontFamilyBold,
    capitalizeFirstLetterInString,
    isGeekCommandCenterUserContext,
} from "../../utils";
import { trakrefBlue } from "../../color_utils";
import Routes from "../../routes";
import UserInfo from "../../UserInfo";

import useGetUserOptions from "../../hooks/useGetUserOptions";
import useQueryParams from "../../hooks/useQueryParams";
import useFormDirty from "../../hooks/useFormDirty";
import UserAddFormUserDetails from "./UserDetailsForm";
import UserAddressForm from "./UserAddressForm";
import UserSettingsForm from "./UserSettingsForm";
import AssignedLocations from "./AssignedLocations";
import Enums from "../../enums";
import useNavigate from "../../hooks/useNavigate";
import useBreadCrumbs from "../../hooks/useBreadCrumbs";

const initialData = {
    id: 0,
    name: "",
    firstName: "",
    lastName: "",
    userName: "",
    email: "",
    state: "",
    isReseller: false,
    country: "US",
    requireAdminApproval: false,
    certificationType: null,
    certificationTypeId: null,
    language: "en-US",
    userType: 1,
    userTypeName: "",
    hasOtherInstances: false,
    notificationTypeId: 1,
    weightUnits: "",
    userPermissions: [],
    company: null,
    address1: null,
    address2: null,
    county: null,
    title: null,
    city: null,
    zip: null,
    phone: null,
    cell: null,
    fax: null,
    headshot: "",
    shortName: "",
    description: null,
    preferredLeakDetectionMethodID: "",
    fkid: null,
    fkName: "",
    countryCode: "+1",
    countryCode2: "+1",
    instanceAssociationId: null,
    certificationNumber: null,
    filesTableModel: {
        fileTypeId: 0,
        fileName: "",
        contentType: "",
        fileBytes: "",
    },
    restrictedLocations: false,
    assignedLocationIds: [],
    userInstanceAssociationIds: []
};

const initialStateUniqueFields = {};

const technician = Enums.UserTypes.Technician;

const userFormStep = {
    UserDetails: 0,
    UserAddress: 1,
    UserSettings: 2,
    AssignedLocations: 3,
};

const fieldStep = {
    email: userFormStep.UserDetails,
    userName: userFormStep.UserDetails,
};

const formatValue = (val) => {
    if (val && val.length > 0 && val[0] === "+") {
        const i = val.indexOf(" ");
        return val.substring(val.length + 1, i).trimStart();
    }

    return val;
};

const UserAddForm = () => {
    const [loading, setLoading] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [errorExist, setErrorExist] = useState(false);
    const [isEditing, setIsEditing] = useState(false);
    const [isInitialLoading, setInitialLoading] = useState(true);
    const [showExitModal, setShowExitModal] = useState(false);
    const [activeStep, setActiveStep] = useState(userFormStep.UserDetails);

    const [formState, setFormState] = useState(null);
    const [uniqueFieldState, setUniqueFieldState] = useState(
        initialStateUniqueFields
    );

    const { userId } = useParams();
    // US 16905: no location-access editing from the Geek Command Center path — creating a user still needs the step
    const hideAssignedLocations = !!userId && isGeekCommandCenterUserContext();
    const navigate = useNavigate({
        pageName: 'UserAddForm'
    });
    const breadCrumbs = useBreadCrumbs({
        path: { id: 'UserAddForm', text: userId ? "Edit" : "Add New User", link: window.location.pathname }
    });
    const { initializeData, isDirty } = useFormDirty();
    const { isLoading: isOptionsLoading, options } = useGetUserOptions(userId);
    const queryParams = useQueryParams();
    const userTypeId = queryParams.get("usertype");
    const assignedLocations = queryParams.get("assignedLocations");
    const fieldsToIgnore = ['countryCode', 'countryCode2']


    const getMappingFields = (data) => {
        const {
            firstName,
            lastName,
            title,
            description,
            email,
            cell,
            countryCode,
            phone,
            countryCode2,
            fax,
            address1,
            address2,
            city,
            county,
            zip,
            country,
            state,
            userType,
            instanceAssociationId,
            certificationTypeID,
            certificationNumber,
            technicianTruck,
            preferredLeakDetectionMethodID,
            language,
            notificationTypeId,
            weightUnits,
            isReseller,
            hasOtherInstances,
            fkid
        } = data;

        return {
            firstName,
            lastName,
            title,
            description,
            email,
            cell,
            countryCode,
            phone,
            countryCode2,
            fax,
            address1,
            address2,
            city,
            county,
            zip,
            country,
            state,
            userType,
            instanceAssociationId,
            certificationTypeID,
            certificationNumber,
            technicianTruck,
            preferredLeakDetectionMethodID,
            language,
            notificationTypeId,
            weightUnits,
            isReseller,
            hasOtherInstances,
            userInstanceAssociationIds: [data.instanceAssociationId],
            fkid
        };
    };

    const onHandleSwitchStep = (step) => {

        if (step < activeStep) {
            setActiveStep(step);
            return;
        }

        setInitialLoading(false);
        setUniqueFieldState(initialStateUniqueFields);

        const { firstName, lastName, email, state, country, certificationTypeID, certificationNumber, userType, userInstanceAssociationIds } = formState;

        const firstTableFields =
            isFieldValid(firstName, false) ||
            isFieldValid(lastName, false) ||
            isFieldValid(email, false);

        if (activeStep === userFormStep.UserDetails && (firstTableFields || errorExist)) {
            return;
        }

        const secondTabFields = isFieldValid(state, false) || isFieldValid(country, false);

        if (activeStep === userFormStep.UserAddress && (secondTabFields || errorExist)) {
            return;
        }

        // third tab validation
        const validateTechnician = isFieldValid(certificationTypeID, false) || isFieldValid(certificationNumber, false);
        const validateRegionalAdmin = isFieldValid(userInstanceAssociationIds, false);
        
        if (activeStep === userFormStep.UserSettings && 
            ((userType === technician && (validateTechnician || errorExist)) ||
            (userType === Enums.UserTypes.RegionalAdministrator && (validateRegionalAdmin || errorExist)))) {
            return;
        }

        setActiveStep(step);
    };

    const isFieldValid = (value, initialLoading = isInitialLoading) => {
        if (initialLoading) return false;

        return !value || value.length <= 0;
    };

    const isFieldUnique = (name) => {
        const result = uniqueFieldState[name] ? uniqueFieldState[name] : false;
        return result;
    };

    const onHandleCloseForm = () => {
        if (isDirty(getMappingFields(formState))) {
            setShowExitModal(true);
            return;
        }

        navigate.goBack((isEditing ? `/User/${userId}` : "/Users"));
    };

    const onHandleValueChanged = (name, value) => {

        if (name === "firstName" || name === "lastName") {

            const newFormState = {
                ...formState,
                [name]: value
            };

            const fullName = capitalizeFirstLetterInString(`${newFormState.firstName.trim()} ${newFormState.lastName.trim()}`);
            const splitFullName = fullName.split(" ");
            const firstChar = splitFullName.length > 0 ? splitFullName[0].charAt(0) : '';
            const secondChar = splitFullName.length > 1 ? splitFullName[1].charAt(0) : '';

            setFormState(prev => ({
                ...prev,
                [name]: value,
                name: fullName,
                shortName: `${firstChar}${secondChar}`
            }));

            return;
        }

        if (name === "certificationTypeID") {
            const certificationType = options.certificationType.find(x => x.id === value);
            setFormState(prev => ({
                ...prev,
                [name]: value,
                certificationType: certificationType.name
            }));

            return;
        }

        setFormState(prev => ({
            ...prev,
            [name]: value,
        }));
    };

    const onHandleFormSave = async () => {
        try {
            setIsSaving(true);
            setInitialLoading(false);
            setUniqueFieldState(initialStateUniqueFields);

            const {
                firstName,
                lastName,
                email,
                state,
                country,
                assignedLocationIds, // state from assignedLocations page
                restrictedLocations, // state from assignedLocations page
                certificationTypeID,
                certificationNumber,
                userType,
                userInstanceAssociationIds
            } = formState;

            const firstTableFields = (
                isFieldValid(firstName, false) ||
                isFieldValid(lastName, false) ||
                isFieldValid(email, false)
            );

            const secondTabFields = isFieldValid(state, false) || isFieldValid(country, false);

            const validateTechnician = isFieldValid(certificationTypeID, false) || isFieldValid(certificationNumber, false);
            const validateRegionalAdmin = isFieldValid(userInstanceAssociationIds, false);

            if (firstTableFields || errorExist) {
                setActiveStep(userFormStep.UserDetails);
                setIsSaving(false);
                return;
            } else if (secondTabFields || errorExist) {
                setActiveStep(userFormStep.UserAddress);
                setIsSaving(false);
                return;
            } else if (userType === technician && (validateTechnician || errorExist)) {
                setActiveStep(userFormStep.UserSettings);
                setIsSaving(false);
                return;
            } else if (userType === Enums.UserTypes.RegionalAdministrator && (validateRegionalAdmin || errorExist)) {
                setActiveStep(userFormStep.UserSettings);
                setIsSaving(false);
                return;
            }

            if (restrictedLocations && assignedLocationIds && assignedLocationIds.length <= 0) {
                return toast.error('Please select at least one location!', { duration: 4000 });
            }

            const newUserInfo = {
                ...formState,
                cell: formState.cell && `${formState.countryCode} ${formatValue(formState.cell)}`,
                phone: formState.phone && `${formState.countryCode2} ${formatValue(formState.phone)}`,
                restrictedLocations
            };
            
            const createUserInstanceId = sessionStorage.getItem("createUserInstanceId");
            if (createUserInstanceId) {
                newUserInfo["createUserInstanceId"] = createUserInstanceId;
            }

            const route = isEditing
                ? Routes.User.UpdateUserDetails(userId)
                : Routes.User.CreateUser();

            const response = await fetch(route, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify(newUserInfo),
            });

            const data = await response.json();

            if (response.status === 500) {
                toast.error('Something went wrong!', { duration: 5000 });
            } else if (response.status === 400) {
                let earliestStep;
                Object.keys(data).forEach((x) => {
                    setUniqueFieldState({
                        [x]: data[x][0],
                    });
                    const step = fieldStep[x];
                    if (typeof step === 'number' && (earliestStep === undefined || step < earliestStep)) {
                        earliestStep = step;
                    }
                });
                if (earliestStep !== undefined) {
                    setActiveStep(earliestStep);
                }
            } else if (response.status === 200 && data) {
                if (isEditing) {
                    if (userId === UserInfo.userId) {
                        UserInfo.setFirstName(data.firstName);
                        UserInfo.setLastName(data.lastName);
                        UserInfo.setGroupId(data.groupId);
                        UserInfo.setUserCulture(data.culture);
                        UserInfo.setWeightUnits(data.weightUnits);
                    }

                    toast.success('The profile has been successfully updated.', { duration: 5000 });
                    navigate.goBack("/User/" + userId);
                } else {
                    navigate.goBack("/Users");
                    toast.success('A new user has been successfully created', { duration: 5000 });
                }
            }
        } catch (error) {
            console.log('Error', error);
            toast.error('Something went wrong!', { duration: 5000 });
        } finally {
            setIsSaving(false);
        }
    };

    const fetchUserData = useCallback(async () => {
        setLoading(true);
        const requestGetUserDetails = await fetch(
            Routes.User.GetUserDetails(userId)
        );
        const data = await requestGetUserDetails.json();

        const newData = {
            ...formState,
            ...data,
        };

        setLoading(false);
        setFormState(newData);
        initializeData(getMappingFields(newData), fieldsToIgnore);
        if (data.userStatus == 'Deleted') {
            navigate.goBack('/');
        }
    }, [userId]);

    const CTALabel = () => {
        if (isEditing) {
            return "Save User";
        }

        return activeStep === 3 ? "Save User" : "Next";
    };

    const CTA = async () => {
        if (isEditing) {
            return onHandleFormSave();
        }

        activeStep === 3 ? onHandleFormSave() : onHandleSwitchStep(activeStep + 1);
    }

    useEffect(() => {
        if (userId) {
            fetchUserData();
            setIsEditing(true);
            if (assignedLocations && !hideAssignedLocations) setActiveStep(userFormStep.AssignedLocations);
            return;
        }

        const newData = {
            ...initialData
        };
        if (userTypeId) newData['userType'] = parseInt(userTypeId);
        initializeData(getMappingFields(newData), fieldsToIgnore);
        setFormState(newData);
    }, [fetchUserData]);

    if (!formState || loading || isOptionsLoading) {
        return <LoadingBar />;
    }

    return (
        <>
            <BreadCrumbs
                path={breadCrumbs.getSortedBreadCrumbs()}
            />

            <Box
                sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    flexDirection: "row",
                    maxWidth: "76%",
                    marginBottom: "2px"
                }}
            >
                <Box
                    style={{
                        display: "flex",
                        justifyContent: "flex-start",
                        alignItems: "center",
                        flexDirection: "row",
                    }}
                >
                    <IconButton aria-label="close" onClick={onHandleCloseForm}>
                        <CloseIcon />
                    </IconButton>

                    <Typography
                        variant="h5"
                        sx={{ "&.MuiTypography-root": { fontFamily: trakrefFontFamily } }}
                        style={{ color: "#1A173B" }}
                    >
                        {isEditing ? "Edit User" : "Add New User"}
                    </Typography>
                </Box>

                <LoadingButton
                    variant="contained"
                    loading={isSaving}
                    onClick={CTA}
                    sx={{
                        "& .MuiCircularProgress-root": {
                            color: "#FFFFFF",
                            width: "21px !important",
                            height: "21px !important",
                        },
                        "&.MuiButton-root": {
                            fontFamily: trakrefFontFamilyBold,
                            backgroundColor: trakrefBlue,
                            letterSpacing: "1.2px",
                        },
                    }}
                >
                    <span style={{ color: isSaving ? "transparent" : "#fff" }}>
                        {CTALabel()}
                    </span>
                </LoadingButton>
            </Box>

            <Grid
                container
                spacing={2}
                sx={{
                    maxWidth: "75%",
                    ml: "26px",
                }}
            >
                <Grid item xs={12}>
                    <Stepper
                        activeStep={activeStep}
                        steps={[
                            'User Details',
                            'Address',
                            'Settings',
                            ...(hideAssignedLocations ? [] : ['Assigned Locations'])
                        ]}
                        onStepChange={onHandleSwitchStep}
                        hideStepCount
                        sx={{
                            gap: "15px",
                            marginBottom: "25px"
                        }}
                    />
                    <div style={{ display: activeStep === 0 ? "block" : "none" }}>
                        <UserAddFormUserDetails
                            userInfo={formState}
                            options={options}
                            isEditing={isEditing}
                            onHandleValueChanged={onHandleValueChanged}
                            isFieldValid={isFieldValid}
                            isFieldUnique={isFieldUnique}
                            userId={userId}
                            setErrorExist={setErrorExist}
                        />
                    </div>
                    <div style={{ display: activeStep === 1 ? "block" : "none" }}>
                        <UserAddressForm
                            userInfo={formState}
                            options={options}
                            isEditing={isEditing}
                            onHandleValueChanged={onHandleValueChanged}
                            isFieldValid={isFieldValid}
                            isFieldUnique={isFieldUnique}
                            userId={userId}
                            setErrorExist={setErrorExist}
                        />
                    </div>
                    <div style={{ display: activeStep === 2 ? "block" : "none" }}>
                        <UserSettingsForm
                            userInfo={formState}
                            options={options}
                            isEditing={isEditing}
                            onHandleValueChanged={onHandleValueChanged}
                            isFieldValid={isFieldValid}
                            isFieldUnique={isFieldUnique}
                            userId={userId}
                            setErrorExist={setErrorExist}
                        />
                    </div>
                    {/* US 16905: stays mounted while hidden — it seeds the location fields the save payload needs */}
                    <div style={{ display: activeStep === 3 ? "block" : "none" }}>
                        <AssignedLocations
                            comingFromUserForm
                            userInformation={formState}
                            isEditing={isEditing}
                            setFormState={setFormState}
                            navigate={navigate}
                        />
                    </div>
                </Grid>
            </Grid>

            <ExitConfirmationModal
                open={showExitModal}
                close={() => setShowExitModal(false)}
                leaveOnClick={() => navigate.goBack(isEditing ? `/User/${userId}` : "/Users")}
                stayOnClick={() => setShowExitModal(false)}
                isEditing={isEditing}
            />
        </>
    );
};

export default UserAddForm;